import { useEffect, useMemo, useState } from "react";
import { endpoints, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Link2, X, Send, Copy, TrendingUp, ArrowUpDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";

const STATUS_COLOR = {
    pending: "text-[#ffea00]",
    active: "text-[#00e676]",
    sent: "text-[#00e676]",
    failed: "text-[#ff3333]",
    accepted: "text-[#a3a3a3]",
    revoked: "text-[#a3a3a3]",
    expired: "text-[#a3a3a3]",
};

const KIND_LABEL = { email: "Email", share_link: "Share link" };
const KIND_ICON = { email: Mail, share_link: Link2 };
const CHANNEL_LABEL = {
    whatsapp: "WhatsApp",
    sms: "SMS",
    instagram_dm: "Instagram DM",
    email: "Email",
    in_person: "In person",
    other: "Other",
};
const EXPIRY_OPTIONS = [
    { value: 24, label: "24 hours" },
    { value: 72, label: "3 days" },
    { value: 168, label: "7 days" },
];

export default function AdminInvitations() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [kindFilter, setKindFilter] = useState("all");
    const [sortBy, setSortBy] = useState(null); // "unique_clicks" | "conversion_rate" | null
    const [sortDir, setSortDir] = useState("desc");
    const [shareOpen, setShareOpen] = useState(false);
    const [artists, setArtists] = useState([]);

    const load = () => {
        setLoading(true);
        endpoints.adminInvitations().then((d) => setRows(d.invitations)).finally(() => setLoading(false));
    };
    useEffect(load, []);
    useEffect(() => {
        endpoints.adminArtists().then((d) => setArtists(d.artists || d || [])).catch(() => setArtists([]));
    }, []);

    const filtered = useMemo(() => {
        let out = rows;
        if (kindFilter !== "all") out = out.filter((r) => (r.kind || "email") === kindFilter);
        if (sortBy) {
            const dir = sortDir === "asc" ? 1 : -1;
            out = [...out].sort((a, b) => {
                const av = Number(a[sortBy] || 0);
                const bv = Number(b[sortBy] || 0);
                return (av - bv) * dir;
            });
        }
        return out;
    }, [rows, kindFilter, sortBy, sortDir]);

    const toggleSort = (key) => {
        if (sortBy !== key) { setSortBy(key); setSortDir("desc"); return; }
        if (sortDir === "desc") { setSortDir("asc"); return; }
        setSortBy(null); setSortDir("desc");
    };

    const revoke = async (inv) => {
        const label = inv.kind === "share_link" ? "this share link" : `invitation for ${inv.email}`;
        if (!window.confirm(`Revoke ${label}?`)) return;
        try { await endpoints.adminRevokeInvitation(inv.id); toast.success("Invitation revoked"); load(); } catch (e) { toast.error(formatApiError(e)); }
    };
    const resend = async (inv) => {
        try {
            const r = await endpoints.adminResendInvitation(inv.id);
            if (r.email_sent) toast.success("Invitation resent");
            else toast.error(r.delivery_message || "Resend failed");
            if (r.dev_invite_link) toast(`Dev link: ${r.dev_invite_link}`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const copyLink = (inv) => {
        const url = `${window.location.origin}/accept-invite?token=${inv.token}`;
        navigator.clipboard.writeText(url);
        toast.success("Invite link copied");
    };

    const sortIndicator = (key) => sortBy === key ? (sortDir === "desc" ? " ↓" : " ↑") : "";

    return (
        <div className="pm-fade" data-testid="admin-invitations">
            <header className="border-b border-[#2a2a2a] px-4 md:px-8 py-6 flex items-center justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Manage</div>
                    <h1 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl">Invitations</h1>
                </div>
                <div className="flex gap-2 flex-wrap items-center">
                    <div className="flex border border-[#2a2a2a]" role="tablist">
                        {["all", "email", "share_link"].map((k) => (
                            <button
                                key={k}
                                onClick={() => setKindFilter(k)}
                                className={`px-3 py-1 text-[10px] font-mono uppercase ${kindFilter === k ? "bg-[#ff4400] text-black" : "text-[#a3a3a3]"}`}
                                data-testid={`admin-invite-filter-${k}`}
                            >
                                {k === "share_link" ? "Share link" : k}
                            </button>
                        ))}
                    </div>
                    {kindFilter !== "email" && (
                        <div className="flex border border-[#2a2a2a]">
                            <button
                                onClick={() => toggleSort("unique_clicks")}
                                className={`px-3 py-1 text-[10px] font-mono uppercase flex items-center gap-1 ${sortBy === "unique_clicks" ? "bg-[#2a2a2a] text-[#ff4400]" : "text-[#a3a3a3]"}`}
                                data-testid="admin-invite-sort-unique"
                            ><ArrowUpDown className="w-3 h-3" /> Unique{sortIndicator("unique_clicks")}</button>
                            <button
                                onClick={() => toggleSort("conversion_rate")}
                                className={`px-3 py-1 text-[10px] font-mono uppercase flex items-center gap-1 border-l border-[#2a2a2a] ${sortBy === "conversion_rate" ? "bg-[#2a2a2a] text-[#ff4400]" : "text-[#a3a3a3]"}`}
                                data-testid="admin-invite-sort-conv"
                            ><ArrowUpDown className="w-3 h-3" /> Conv{sortIndicator("conversion_rate")}</button>
                        </div>
                    )}
                    <Dialog open={shareOpen} onOpenChange={setShareOpen}>
                        <DialogTrigger asChild>
                            <button className="pm-btn pm-btn-primary" data-testid="admin-create-share-link-btn">
                                <Link2 className="w-4 h-4 mr-2" /> Create share link
                            </button>
                        </DialogTrigger>
                        <AdminShareLinkDialog artists={artists} onDone={() => { setShareOpen(false); load(); }} />
                    </Dialog>
                </div>
            </header>
            <div className="px-2 md:px-4">
                {loading ? <div className="p-8 font-mono text-xs text-[#666]">Loading…</div>
                : filtered.length === 0 ? <div className="p-8 font-mono text-xs text-[#666]" data-testid="admin-invitations-empty">No invitations.</div>
                : (
                    <div className="border border-[#2a2a2a] mt-3">
                        {filtered.map((i) => {
                            const KindIcon = KIND_ICON[i.kind || "email"] || Mail;
                            const isShare = i.kind === "share_link";
                            const workspace = i.artist_name || i.venue_name || i.artist_id || i.venue_id || "—";
                            return (
                                <div key={i.id} className={`grid grid-cols-12 gap-3 items-center p-3 border-b border-[#2a2a2a] last:border-b-0 text-sm ${isShare ? "bg-[#0d0d0d]" : ""}`} data-testid={`admin-invite-row-${i.id}`}>
                                    <div className="col-span-12 md:col-span-3 flex items-center gap-2">
                                        <KindIcon className={`w-3 h-3 ${isShare ? "text-[#ff9500]" : "text-[#ff4400]"}`} />
                                        <div className="min-w-0">
                                            <div className="font-mono text-xs truncate" data-testid={`admin-invite-target-${i.id}`}>
                                                {isShare ? (i.event_names?.[0] ? `Link · ${i.event_names[0]}` : "Share link") : i.email}
                                            </div>
                                            <div className="text-[10px] font-mono uppercase text-[#666]" data-testid={`admin-invite-kind-${i.id}`}>
                                                {KIND_LABEL[i.kind || "email"]}
                                                {i.created_by_name ? ` · by ${i.created_by_name}` : ""}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-span-6 md:col-span-2 text-[10px] font-mono uppercase text-[#a3a3a3] truncate">{workspace}</div>
                                    <div className="col-span-3 md:col-span-1 text-[10px] font-mono uppercase text-[#a3a3a3]">{i.role}</div>
                                    {isShare ? (
                                        <>
                                            <div className="col-span-6 md:col-span-2 text-[10px] font-mono uppercase text-[#a3a3a3]" data-testid={`admin-invite-analytics-${i.id}`}>
                                                <TrendingUp className="w-3 h-3 inline mr-1" />{i.total_clicks || 0} · {i.unique_clicks || 0} uniq · {i.conversion_rate || 0}%
                                            </div>
                                            <div className="col-span-3 md:col-span-1 text-[10px] font-mono uppercase text-[#ff9500] truncate" data-testid={`admin-invite-channel-${i.id}`}>
                                                {i.channel ? (CHANNEL_LABEL[i.channel] || i.channel) : "—"}
                                            </div>
                                        </>
                                    ) : (
                                        <div className="col-span-9 md:col-span-3 text-[10px] font-mono uppercase text-[#666] truncate">
                                            {i.accepted_by_name ? `by ${i.accepted_by_name}` : `exp ${i.expires_at?.slice(0, 10)}`}
                                        </div>
                                    )}
                                    {isShare && (
                                        <div className="col-span-6 md:col-span-2 text-[10px] font-mono uppercase text-[#666] truncate" data-testid={`admin-invite-last-${i.id}`}>
                                            {i.last_clicked_at ? `last ${i.last_clicked_at.slice(0, 10)}` : (i.accepted_by_name ? `by ${i.accepted_by_name}` : `exp ${i.expires_at?.slice(0, 10)}`)}
                                        </div>
                                    )}
                                    <div className={`col-span-6 md:col-span-1 text-[10px] font-mono uppercase ${STATUS_COLOR[i.status] || "text-[#a3a3a3]"}`} data-testid={`admin-invite-status-${i.id}`}>
                                        {i.status}
                                    </div>
                                    <div className="col-span-12 md:col-span-2 flex justify-end gap-1 flex-wrap">
                                        {(i.status === "pending" || i.status === "active") && (
                                            <>
                                                {isShare ? (
                                                    <button onClick={() => copyLink(i)} className="pm-btn text-[10px]" data-testid={`admin-invite-copy-${i.id}`}><Copy className="w-3 h-3 mr-1" /> Link</button>
                                                ) : (
                                                    <button onClick={() => resend(i)} className="pm-btn text-[10px]" data-testid={`admin-invite-resend-${i.id}`}><Send className="w-3 h-3 mr-1" /> Resend</button>
                                                )}
                                                <button onClick={() => revoke(i)} className="pm-btn text-[10px]" data-testid={`admin-invite-revoke-${i.id}`}><X className="w-3 h-3 mr-1" /> Revoke</button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

function AdminShareLinkDialog({ artists, onDone }) {
    const [artistId, setArtistId] = useState("");
    const [role, setRole] = useState("team");
    const [expiresHours, setExpiresHours] = useState(72);
    const [busy, setBusy] = useState(false);
    const [created, setCreated] = useState(null);
    const [events, setEvents] = useState([]);
    const [eventId, setEventId] = useState("");

    useEffect(() => {
        if (!artistId || role !== "staff") { setEvents([]); setEventId(""); return; }
        endpoints.workspaceEvents(artistId).then((d) => setEvents(d.all || [])).catch(() => setEvents([]));
    }, [artistId, role]);

    const submit = async () => {
        if (!artistId) { toast.error("Select an artist"); return; }
        if (role === "staff" && !eventId) { toast.error("Select an event"); return; }
        setBusy(true);
        try {
            const payload = { role, expires_in_hours: expiresHours };
            if (role === "staff") payload.event_id = eventId;
            const r = await endpoints.adminCreateShareLink(artistId, payload);
            const url = `${window.location.origin}/accept-invite?token=${r.token}`;
            setCreated({ url, ...r });
            toast.success("Share link created");
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    };
    const copyCreated = () => {
        if (!created) return;
        navigator.clipboard.writeText(created.url);
        toast.success("Link copied");
    };
    const close = () => { setCreated(null); setArtistId(""); setRole("team"); setEventId(""); setExpiresHours(72); onDone(); };

    return (
        <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] rounded-none max-w-md">
            <DialogHeader><DialogTitle className="font-display uppercase text-2xl">Admin share link</DialogTitle></DialogHeader>
            {!created ? (
                <>
                    <div className="space-y-4">
                        <label className="block">
                            <span className="text-[10px] font-mono uppercase tracking-widest text-[#a3a3a3]">Artist</span>
                            <select value={artistId} onChange={(e) => setArtistId(e.target.value)} className="w-full mt-1 bg-transparent border-b border-[#2a2a2a] py-2 outline-none font-mono uppercase text-xs" data-testid="admin-share-artist">
                                <option value="" className="bg-[#141414]">Select artist…</option>
                                {artists.map((a) => (
                                    <option key={a.id} value={a.id} className="bg-[#141414]">{a.name}</option>
                                ))}
                            </select>
                        </label>
                        <label className="block">
                            <span className="text-[10px] font-mono uppercase tracking-widest text-[#a3a3a3]">Role</span>
                            <select value={role} onChange={(e) => setRole(e.target.value)} className="w-full mt-1 bg-transparent border-b border-[#2a2a2a] py-2 outline-none font-mono uppercase text-xs" data-testid="admin-share-role">
                                <option value="team" className="bg-[#141414]">Team Member</option>
                                <option value="staff" className="bg-[#141414]">Event Staff</option>
                            </select>
                        </label>
                        {role === "staff" && (
                            <label className="block">
                                <span className="text-[10px] font-mono uppercase tracking-widest text-[#a3a3a3]">Event</span>
                                <select value={eventId} onChange={(e) => setEventId(e.target.value)} className="w-full mt-1 bg-transparent border-b border-[#2a2a2a] py-2 outline-none font-mono uppercase text-xs" data-testid="admin-share-event">
                                    <option value="" className="bg-[#141414]">Select event…</option>
                                    {events.map((e) => <option key={e.id} value={e.id} className="bg-[#141414]">{e.name}</option>)}
                                </select>
                            </label>
                        )}
                        <label className="block">
                            <span className="text-[10px] font-mono uppercase tracking-widest text-[#a3a3a3]">Expires in</span>
                            <select value={expiresHours} onChange={(e) => setExpiresHours(Number(e.target.value))} className="w-full mt-1 bg-transparent border-b border-[#2a2a2a] py-2 outline-none font-mono uppercase text-xs" data-testid="admin-share-expiry">
                                {EXPIRY_OPTIONS.map((o) => <option key={o.value} value={o.value} className="bg-[#141414]">{o.label}</option>)}
                            </select>
                        </label>
                    </div>
                    <DialogFooter>
                        <button onClick={submit} disabled={busy} className="pm-btn pm-btn-primary" data-testid="admin-share-submit"><Link2 className="w-4 h-4 mr-2" /> Generate link</button>
                    </DialogFooter>
                </>
            ) : (
                <>
                    <div className="space-y-3">
                        <div className="border border-[#2a2a2a] p-3 bg-[#141414] font-mono text-xs break-all" data-testid="admin-share-url">{created.url}</div>
                        <div className="text-[10px] font-mono uppercase text-[#666]">Expires: {new Date(created.expires_at).toLocaleString()}</div>
                    </div>
                    <DialogFooter>
                        <button onClick={copyCreated} className="pm-btn pm-btn-primary" data-testid="admin-share-copy"><Copy className="w-4 h-4 mr-2" /> Copy link</button>
                        <button onClick={close} className="pm-btn" data-testid="admin-share-done">Done</button>
                    </DialogFooter>
                </>
            )}
        </DialogContent>
    );
}
